import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Upload, 
  Users, 
  QrCode, 
  Lock, 
  Clock, 
  Share2, 
  Image, 
  Star, 
  Shield, 
  Check, 
  ArrowRight, 
  Camera,
  Download,
  Calendar,
  Smartphone,
  Infinity,
  LayoutGrid,
  Layers,
  ChevronRight
} from 'lucide-react';

const HomePage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Hero Section with Minimal Design */}
      <section className="pt-32 pb-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-3 py-1 bg-[#FFEEEE] text-[#FF4444] font-medium text-sm mb-6 rounded-full">
            EVENT PHOTO SHARING PLATFORM
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-8 leading-tight text-[#FF4444]">
            Capture Every Moment Together
          </h1>
          <p className="text-gray-600 text-lg mb-10 max-w-2xl mx-auto">
            Create beautiful collaborative photo collections for your events. Everyone contributes, everyone enjoys — real-time photo sharing made simple.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link to="/register" className="bg-[#FF4444] text-white px-8 py-3 rounded-md font-medium hover:bg-[#FF3333] transition-colors">
              Start for Free
            </Link>
            <Link to="/pricing" className="border border-gray-200 px-8 py-3 rounded-md font-medium hover:bg-gray-50 transition-colors">
              View Pricing
            </Link>
          </div>
          
          {/* Enhanced UI Card */}
          <div className="max-w-3xl mx-auto bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between bg-gradient-to-r from-[#FFEEEE] to-[#FFF5F5] px-6 py-4">
              <div className="flex items-center">
                <div className="bg-white rounded-full p-2 mr-3 shadow-sm">
                  <Camera className="w-5 h-5 text-[#FF4444]" />
                </div>
                <span className="font-medium text-gray-800">Wedding Gallery</span>
              </div>
              <div className="flex items-center bg-white rounded-full px-3 py-1 text-xs text-[#FF4444] shadow-sm">
                <Image className="w-3.5 h-3.5 mr-1.5" />
                <span>42 photos</span>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="aspect-square rounded-lg overflow-hidden shadow-sm col-span-2 row-span-2">
                  <img src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=400" alt="Wedding photo" className="h-full w-full object-cover" />
                </div>
                <div className="aspect-square rounded-lg overflow-hidden shadow-sm">
                  <img src="https://images.unsplash.com/photo-1606800052052-a08af7148866?q=80&w=200" alt="Wedding photo" className="h-full w-full object-cover" />
                </div>
                <div className="aspect-square rounded-lg overflow-hidden shadow-sm">
                  <img src="https://images.unsplash.com/photo-1494955870715-979ca4f13bf0?q=80&w=200" alt="Wedding photo" className="h-full w-full object-cover" />
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex -space-x-3">
                  <div className="w-8 h-8 rounded-full bg-[#FFDDDD] border-2 border-white flex items-center justify-center text-[#FF6666] text-xs font-medium shadow-sm">JD</div>
                  <div className="w-8 h-8 rounded-full bg-[#FFEEEE] border-2 border-white flex items-center justify-center text-[#FF4444] text-xs font-medium shadow-sm">MK</div>
                  <div className="w-8 h-8 rounded-full bg-[#FFF5F5] border-2 border-white flex items-center justify-center text-[#FF8888] text-xs font-medium shadow-sm">AS</div>
                  <div className="w-8 h-8 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center text-gray-400 text-xs font-medium">+5</div>
                </div>
                <button className="text-sm text-[#FF4444] flex items-center px-3 py-1.5 bg-[#FFEEEE] rounded-full hover:bg-[#FFE5E5] transition-colors">
                  View Gallery <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-14 border-b border-gray-100 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="text-4xl font-bold text-gray-900 mb-2">3K+</div>
              <div className="text-gray-500 text-sm">Active Users</div>
            </div>
            <div className="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="text-4xl font-bold text-gray-900 mb-2">2K+</div>
              <div className="text-gray-500 text-sm">Events Created</div>
            </div>
            <div className="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="text-4xl font-bold text-gray-900 mb-2">210K+</div>
              <div className="text-gray-500 text-sm">Photos Shared</div>
            </div>
            <div className="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="text-4xl font-bold text-gray-900 mb-2">100%</div>
              <div className="text-gray-500 text-sm">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 border-b border-gray-100 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-[#FFEEEE] text-[#FF4444] font-medium text-sm mb-4 rounded-full">
              POWERFUL FEATURES
            </div>
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Everything you need for seamless photo sharing</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our platform combines simplicity with powerful features designed to make event photo collection effortless
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Upload className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">Real-time Uploads</h3>
              <p className="text-gray-600 text-sm">Share photos instantly during your event, keeping everyone engaged in real-time.</p>
            </div>
            <div className="p-6 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">Collaborative Sharing</h3>
              <p className="text-gray-600 text-sm">Let all guests contribute their photos to create a complete collection of memories.</p>
            </div>
            <div className="p-6 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">Private & Secure</h3>
              <p className="text-gray-600 text-sm">Control who can view and upload photos with customizable privacy settings.</p>
            </div>
            <div className="p-6 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <QrCode className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">Easy Sharing</h3>
              <p className="text-gray-600 text-sm">Generate QR codes for quick access, making it effortless for guests to join.</p>
            </div>
            <div className="p-6 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Image className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">High Quality</h3>
              <p className="text-gray-600 text-sm">Store and share photos in their original quality without compression.</p>
            </div>
            <div className="p-6 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">Event Timeline</h3>
              <p className="text-gray-600 text-sm">Organize photos chronologically, creating a beautiful timeline of your event.</p>
            </div>
          </div>
          
          <div className="text-center mt-10">
            <Link to="/features" className="inline-flex items-center text-[#FF4444] hover:text-[#FF3333] font-medium text-sm group">
              Explore all features
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-gray-50 border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-[#FFEEEE] text-[#FF4444] font-medium text-sm mb-4 rounded-full">
              SIMPLE PROCESS
            </div>
            <h2 className="text-3xl font-bold mb-4 text-gray-900">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our simple process makes it easy to create, collect, and share all your event photos
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">1. Create Event</h3>
              <p className="text-sm text-gray-600">
                Set up your event in seconds with a custom name, date, and privacy settings.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <QrCode className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">2. Share QR Code</h3>
              <p className="text-sm text-gray-600">
                Share your unique QR code with guests for easy access to the photo gallery.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Upload className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">3. Collect Photos</h3>
              <p className="text-sm text-gray-600">
                You and your guests can upload photos directly to the shared gallery.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Download className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">4. Download & Share</h3>
              <p className="text-sm text-gray-600">
                Download individual photos or the entire collection with just a few clicks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section Preview */}
      <section className="py-20 px-4 border-b border-gray-100 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-[#FFEEEE] text-[#FF4444] font-medium text-sm mb-4 rounded-full">
              TRANSPARENT CREDIT-BASED PRICING
            </div>
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Simple pricing for all your photo needs</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              No subscriptions, no hidden fees. Just pay for the photos you upload and create as many events as you need.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Basic Plan */}
            <div className="relative bg-white rounded-xl border border-gray-200">
              <div className="px-6 pt-6 pb-6">
                <h3 className="text-xl font-bold mb-1 text-gray-900">Basic</h3>
                <p className="text-gray-500 text-sm mb-5">Perfect for small gatherings and family events</p>

                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-bold text-gray-900">$19.99</span>
                  <span className="text-gray-500 text-sm">one-time</span>
                </div>
                
                <div className="bg-[#FFEEEE] rounded-lg p-3 mb-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Image className="w-5 h-5 text-[#FF4444]" />
                      <span className="text-sm font-medium">100 image credits</span>
                    </div>
                    <span className="text-xs bg-white px-2 py-1 rounded-full text-[#FF4444] font-medium">
                      $0.20/photo
                    </span>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="mb-3 text-sm text-gray-700 font-medium">Includes:</div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">Unlimited events</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">3 months event hosting</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">QR code for easy sharing</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">Guest photo uploads</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="px-6 py-5 border-t border-gray-100">
                <Link
                  to="/register"
                  className="w-full py-3 px-4 rounded-lg text-center font-medium transition-colors block border border-gray-200 text-gray-900 hover:bg-gray-50"
                >
                  Get Started
                </Link>
              </div>
            </div>

            {/* Standard Plan */}
            <div className="relative bg-white rounded-xl border-2 border-[#FF4444]">
              <div className="absolute -top-4 left-0 right-0 mx-auto w-32 bg-gradient-to-r from-[#FF4444] to-[#FF8080] text-white text-xs font-medium text-center py-1 rounded-full">
                MOST POPULAR
              </div>

              <div className="px-6 pt-8 pb-6">
                <h3 className="text-xl font-bold mb-1 text-gray-900">Standard</h3>
                <p className="text-gray-500 text-sm mb-5">Ideal for weddings and larger celebrations</p>

                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-bold text-gray-900">$39.99</span>
                  <span className="text-gray-500 text-sm">one-time</span>
                </div>
                
                <div className="bg-[#FFEEEE] rounded-lg p-3 mb-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Image className="w-5 h-5 text-[#FF4444]" />
                      <span className="text-sm font-medium">300 image credits</span>
                    </div>
                    <span className="text-xs bg-white px-2 py-1 rounded-full text-[#FF4444] font-medium">
                      $0.13/photo
                    </span>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="mb-3 text-sm text-gray-700 font-medium">Includes:</div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">Unlimited events</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">3 months event hosting</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">QR code for easy sharing</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">Customizable gallery</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="px-6 py-5 border-t border-[#FF4444]/10">
                <Link
                  to="/register"
                  className="w-full py-3 px-4 rounded-lg text-center font-medium transition-colors block bg-gradient-to-r from-[#FF4444] to-[#FF6666] text-white hover:from-[#FF3333] hover:to-[#FF5555]"
                >
                  Choose Standard
                </Link>
              </div>
            </div>

            {/* Professional Plan */}
            <div className="relative bg-white rounded-xl border border-gray-200">
              <div className="px-6 pt-6 pb-6">
                <h3 className="text-xl font-bold mb-1 text-gray-900">Professional</h3>
                <p className="text-gray-500 text-sm mb-5">For photographers and event planners</p>

                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-bold text-gray-900">$59.99</span>
                  <span className="text-gray-500 text-sm">one-time</span>
                </div>
                
                <div className="bg-[#FFEEEE] rounded-lg p-3 mb-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Image className="w-5 h-5 text-[#FF4444]" />
                      <span className="text-sm font-medium">500 image credits</span>
                    </div>
                    <span className="text-xs bg-white px-2 py-1 rounded-full text-[#FF4444] font-medium">
                      $0.12/photo
                    </span>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="mb-3 text-sm text-gray-700 font-medium">Includes:</div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">Unlimited events</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">3 months event hosting</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">Bulk download</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">Priority support</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="px-6 py-5 border-t border-gray-100">
                <Link
                  to="/register"
                  className="w-full py-3 px-4 rounded-lg text-center font-medium transition-colors block border border-gray-200 text-gray-900 hover:bg-gray-50"
                >
                  Choose Professional
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/pricing" className="inline-flex items-center text-[#FF4444] hover:text-[#FF3333] font-medium text-sm group">
              View full pricing details
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 border-b border-gray-100 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-[#FFEEEE] text-[#FF4444] font-medium text-sm mb-4 rounded-full">
              TESTIMONIALS
            </div>
            <h2 className="text-3xl font-bold mb-4 text-gray-900">What our customers say</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Trusted by event planners, wedding photographers, and families worldwide
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow transition-all">
              <div className="flex mb-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 text-[#FF4444] fill-[#FF4444]" />
                ))}
              </div>
              <p className="text-gray-600 text-sm mb-6">
                "OurPhotos made our wedding day even more special. All our guests contributed to our collection, creating memories we'll cherish forever."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#FFEEEE] rounded-full mr-3 flex items-center justify-center">
                  <span className="text-[#FF4444] font-medium">SJ</span>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-900">Sarah Johnson</h4>
                  <p className="text-xs text-gray-500">Wedding Host</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow transition-all">
              <div className="flex mb-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 text-[#FF4444] fill-[#FF4444]" />
                ))}
              </div>
              <p className="text-gray-600 text-sm mb-6">
                "The real-time sharing feature was a game-changer for our corporate event. Our team was immediately engaged and the process was seamless."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#FFEEEE] rounded-full mr-3 flex items-center justify-center">
                  <span className="text-[#FF4444] font-medium">MT</span>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-900">Mark Thompson</h4>
                  <p className="text-xs text-gray-500">Event Planner</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow transition-all">
              <div className="flex mb-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 text-[#FF4444] fill-[#FF4444]" />
                ))}
              </div>
              <p className="text-gray-600 text-sm mb-6">
                "The ease of use and quality of the service exceeded our expectations. Our family reunion photos have never been so well organized."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#FFEEEE] rounded-full mr-3 flex items-center justify-center">
                  <span className="text-[#FF4444] font-medium">ED</span>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-900">Emma Davis</h4>
                  <p className="text-xs text-gray-500">Family Reunion Organizer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF4444] to-[#FF6666] z-0"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl font-bold mb-4 text-white">Ready to start collecting memories?</h2>
          <p className="mb-10 text-white text-lg opacity-90">
            Join thousands of event hosts who trust OurPhotos with their precious moments.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="bg-white text-[#FF4444] px-8 py-3 rounded-md font-medium hover:shadow-lg transition-all">
              Create Your First Event
            </Link>
            <Link to="/pricing" className="border border-white text-white px-8 py-3 rounded-md font-medium hover:bg-white/10 transition-all">
              Explore Pricing Options
            </Link>
          </div>
          <p className="mt-6 text-sm text-white opacity-75">
            No credit card required to get started • Free plan available
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;